import React, { useState, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { usePopper } from 'react-popper';
import './style.scss';

import Toggle from './toggle';
import Content from './content';
import { DropdownContext } from './context';

let dropdownIdCounter = 0;
const getNextDropdownId = () => {
  dropdownIdCounter += 1;
  return dropdownIdCounter;
};

const ContextMenu = ({
  className,
  optionsClassName,
  isLoading,
  placement = 'bottom',
  value,
  visible,
  onVisibleChange = () => { },
  onBlur = () => { },
  hideOnBlur = true,
  modifiers,
  children,
  optionsWrapperRef = null,
  menuLabel,
  toggleLabel,
  menuRole = 'menu',
}) => {
  const dropdownIdRef = useRef(`dropdown-menu-${getNextDropdownId()}`);
  const dropdownId = dropdownIdRef.current;
  const contextValue = useMemo(() => ({ dropdownId, visible }), [dropdownId, visible]);
  const referenceRef = useRef();
  const popperRef = useRef(null);

  const { styles, attributes, forceUpdate } = usePopper(referenceRef.current, popperRef.current, {
    placement,
    modifiers,
  });

  const [frontRefreshed, setFrontRefreshed] = useState(false);

  const show = () => onVisibleChange(true);
  const hide = () => onVisibleChange(false);

  const handleBlur = () => {
    if(hideOnBlur) {
      hide();
      referenceRef.current?.focus();
    }
    onBlur();
  }

  useEffect(() => {
    // Reset frontRefreshed when visibility changes to false
    if (!visible) {
      setFrontRefreshed(false);
    }
    // Only update when becoming visible
    else if (visible && !frontRefreshed && forceUpdate) {
      // Schedule the update for the next frame
      requestAnimationFrame(() => {
        forceUpdate();
        setFrontRefreshed(true);
      });
    }
  }, [visible, frontRefreshed, forceUpdate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && visible) {
        hide();
        referenceRef.current?.focus();
      }
    };
    if (visible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [visible]);

  useEffect(() => {
    if (visible) {
      requestAnimationFrame(() => {
        const menu = document.getElementById(dropdownId);
        const itemSelector = menuRole === 'listbox' ? '[role="option"]' : '[role="menuitem"]';
        const firstItem = menu?.querySelector(itemSelector);
        firstItem?.focus();
      });
    }
  }, [visible]);

  return (
    <DropdownContext.Provider value={contextValue}>
      <button
        type="button"
        ref={referenceRef}
        onClick={visible ? hide : show}
        aria-expanded={visible}
        aria-haspopup={menuRole === 'listbox' ? 'listbox' : 'menu'}
        aria-controls={dropdownId}
        aria-label={toggleLabel}
        className={cx('c-dropdown', {
          'c-dropdown--loading': isLoading,
          'c-dropdown--placeholder': isEmpty(value),
          [className]: !!className,
        })}
      >
        {children.filter((child) => child.type === Toggle)}
      </button>

      {visible && (<div className="c-dropdown__overlay" onClick={handleBlur} role="presentation" />)}

      {!visible && (
        <div ref={popperRef} className="c-dropdown--invisible c-dropdown__options">
          {children.filter((child) => child.type === Content)}
        </div>
      )}

      {visible
        ? ReactDOM.createPortal(
          <>

            <div
              id={dropdownId}
              ref={popperRef}
              role={menuRole}
              aria-label={menuLabel}
              className={cx('c-dropdown__options', {
                [optionsClassName]: !!optionsClassName,
              })}
              style={styles.popper}
              {...attributes.popper}
            >
              <div
                className={cx('c-dropdown__options__wrapper', {
                  visible,
                })}
                style={styles.offset}
                ref={optionsWrapperRef}
              >
                {children.filter((child) => child.type === Content)}
              </div>
            </div>
          </>,
          document.body
        )
        : null}
    </DropdownContext.Provider>
  );
};

export default ContextMenu;
